import L, { type LatLngExpression } from 'leaflet';
import { useEffect, useRef, useState } from 'react';
import { MapContainer, Marker, TileLayer, useMapEvents } from 'react-leaflet';
import {
  BRAZILIAN_STATES,
  BRAZILIAN_STATE_INFO,
  brazilianStateName,
  stateOfCep,
} from '@evogard/domain';
import { Alert } from './Alert.js';
import { Button, Spin } from './Button.js';
import { Field } from './Field.js';
import { FormRow } from './Form.js';
import { Icon } from './Icon.js';
import { Modal } from './Modal.js';
import { Select } from './Select.js';

/**
 * O campo de endereço — CEP, a consulta ao ViaCEP, os campos pra completar ou
 * corrigir à mão, e a posição marcada no mapa.
 *
 * ## Por que os campos continuam editáveis depois da consulta
 *
 * Quem preenche pode não ter o CEP em mãos — sabe a rua, o bairro, a cidade — e
 * o ViaCEP nunca soube dizer NENHUM CEP a partir de um endereço parcial digitado
 * por aqui (a busca por endereço dele existe, mas pede rua completa e cidade
 * exatas, e devolve uma LISTA de CEPs candidatos, não um). Por isso a consulta é
 * um atalho que preenche o que sabe, nunca a única porta: logradouro, número,
 * complemento, bairro, cidade e estado continuam campos de verdade, e o CEP
 * pode ficar em branco.
 *
 * `numero` não é um espelho do ViaCEP — ele não devolve número nenhum, porque
 * um CEP cobre um trecho de rua inteiro. É o único campo do endereço que a
 * pessoa digita sempre, consulta tendo dado certo ou não.
 *
 * ## Por que ele sempre devolve o ViaCEP inteiro
 *
 * O ViaCEP responde treze campos, e este componente não escolhe quais valem: a
 * resposta pode carregar um `complemento` que a tela de hoje não mostra e a de
 * amanhã precisa (a faixa de numeração, por exemplo). Descartar campo agora é
 * quem consome ter que voltar aqui para recuperá-lo depois — `onChange` entrega
 * o estado inteiro, sempre, e quem lê escolhe o que usa.
 *
 * ## O pino nasce do endereço, quando dá (29/10/2026)
 *
 * Com `geocode`, o campo pede a posição assim que o endereço fica completo —
 * logradouro, número, cidade e estado —, ao sair de um campo ou depois do CEP.
 * Uma vez por endereço, nunca a cada tecla: cada pedido é uma chamada paga ao
 * Google. Três regras decidem o que entra:
 *
 * - **Só o ponto entra sozinho.** Quando o Google acha só a região (o centro da
 *   rua, do CEP, da cidade), o pino não é gravado: o mapa abre ali e a pessoa
 *   marca. A base de um prestador no centro de Caucaia ordena a chegada errado
 *   sem erro nenhum.
 * - **O pino marcado à mão manda.** Mudar o endereço depois não o move — avisa,
 *   e oferece trocar pela posição do endereço.
 * - **O pino que veio do endereço acompanha o endereço.** Mudou o endereço, ele
 *   sai e é procurado de novo; nunca fica apontando para o anterior.
 *
 * O pino que o registro já tinha ao abrir conta como marcado à mão: não se sabe
 * de onde veio, e trocá-lo sozinho seria desfazer a correção de alguém.
 *
 * ## Por que o pino é nosso, não o do Leaflet
 *
 * O marcador padrão do Leaflet resolve a própria imagem por um caminho que o
 * empacotador não enxerga — o defeito conhecido de todo `react-leaflet` com
 * Vite ou Webpack, ícone quebrado até alguém reescrever a URL à mão. Desenhar o
 * pino como `divIcon` com o próprio SVG da marca evita o problema inteiro: sem
 * imagem para resolver, sem CDN externo, e o pino sai na cor da casa.
 */
// @fornecedor — de `cep` a `siafi` são os nomes que o ViaCEP escolheu (é o
// próprio contrato de resposta, documentado em viacep.com.br); não traduz.
// `numero`, `latitude` e `longitude` são nossos, e ficam junto por serem parte
// do mesmo endereço.
export interface Address {
  cep: string;
  logradouro: string;
  numero: string;
  complemento: string;
  unidade: string;
  bairro: string;
  localidade: string;
  uf: string;
  estado: string;
  regiao: string;
  ibge: string;
  gia: string;
  ddd: string;
  siafi: string;
  latitude: number | null;
  longitude: number | null;
}

// @fornecedor — os campos espelham `Address`, que já é do ViaCEP; não traduz
const EMPTY_ADDRESS: Address = {
  cep: '',
  logradouro: '',
  numero: '',
  complemento: '',
  unidade: '',
  bairro: '',
  localidade: '',
  uf: '',
  estado: '',
  regiao: '',
  ibge: '',
  gia: '',
  ddd: '',
  siafi: '',
  latitude: null,
  longitude: null,
};

// @fornecedor — o corpo exato que o ViaCEP devolve
/** O ViaCEP responde 200 com `{ erro: true }` — não é HTTP que marca a falha, é o corpo. */
interface ViaCepResponse {
  erro?: true;
  cep?: string;
  logradouro?: string;
  complemento?: string;
  unidade?: string;
  bairro?: string;
  localidade?: string;
  uf?: string;
  estado?: string;
  regiao?: string;
  ibge?: string;
  gia?: string;
  ddd?: string;
  siafi?: string;
}

function formatCep(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 8);
  return digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
}

type Status = 'ocioso' | 'buscando' | 'encontrado' | 'nao_encontrado' | 'falhou';

/** A posição que o endereço deu — o que `AddressField` pede a quem tem o Google. */
export interface GeocodeResult {
  lat: number;
  lng: number;
  /** O ponto, e não só a região (o centro da rua, do CEP ou da cidade). */
  precise: boolean;
}

type Locating =
  | 'idle'
  | 'searching'
  | 'found'
  | 'approximate'
  | 'notFound'
  | 'failed'
  | 'addressChanged';

/** O aviso de cada desfecho que pede o mapa; os outros não avisam nada. */
const LOCATING_NOTICE: Partial<Record<Locating, string>> = {
  approximate:
    'O Google achou só a região deste endereço, e não o ponto. Marque a posição no mapa — ele abre ali perto.',
  notFound: 'O Google não achou este endereço. Marque a posição no mapa.',
  failed: 'Não deu para achar a posição pelo endereço agora. Marque a posição no mapa.',
  addressChanged:
    'O endereço mudou depois que o pino foi marcado à mão. Confira no mapa, ou troque o pino pela posição do novo endereço.',
};

/**
 * O endereço em uma linha, para o Google — ou `null` enquanto faltar o que
 * identifica o lugar. Sem o número o Google devolve o centro da rua, que não
 * serve de posição; sem cidade e estado, a rua de outra cidade.
 */
export function addressLine(a: Address): string | null {
  const street = a.logradouro.trim();
  const number = a.numero.trim();
  const city = a.localidade.trim();
  if (!street || !number || !city || !a.uf) return null;
  const district = a.bairro.trim();
  const cep = a.cep.replace(/\D/g, '');
  return [
    `${street}, ${number}`,
    district,
    `${city} - ${a.uf}`,
    cep.length === 8 ? cep : '',
    'Brasil',
  ]
    .filter(Boolean)
    .join(', ');
}

/**
 * Consulta um CEP no ViaCEP — o atalho que o `AddressField` usa, e que o
 * formulário do portal usa sem o mapa (19/10/2026). Devolve o corpo inteiro do
 * ViaCEP, `'nao_encontrado'` quando ele responde `{ erro: true }`, e `null`
 * quando o CEP ainda não tem oito dígitos. Falha de rede LANÇA: quem chama
 * decide o que dizer.
 *
 * Quem chama do navegador precisa de `https://viacep.com.br` no `connect-src`
 * da CSP — o portal declara em `apps/portal/nginx-headers.conf`.
 */
export async function lookupCep(
  raw: string,
): Promise<Omit<ViaCepResponse, 'erro'> | 'nao_encontrado' | null> {
  const digits = raw.replace(/\D/g, '');
  if (digits.length !== 8) return null;
  const response = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
  const body = (await response.json()) as ViaCepResponse;
  return body.erro ? 'nao_encontrado' : body;
}

export function AddressField({
  title,
  required,
  onChange,
  initialValue,
  geocode,
}: {
  /** O que este endereço é, quando a tela tem mais de um — "Endereço do ocorrido". */
  title?: string;
  /** Marca Logradouro, Cidade e Estado — os três que identificam um lugar. */
  required?: boolean;
  /** O endereço inteiro — disparado a cada edição, do CEP ao pino no mapa. */
  onChange?: (address: Address) => void;
  /**
   * O endereço com que o campo ABRE — para corrigir um registro que já tem
   * endereço. Lido só na montagem, como o `defaultValue` de um `<input>`: o
   * campo continua sendo dono do que a pessoa digita depois.
   */
  initialValue?: Partial<Address>;
  /**
   * Acha a posição do endereço — `null` quando não acha, e LANÇA quando não
   * deu para perguntar. Sem ele o pino é só o do mapa, como era. O pacote não
   * fala com a API: quem tem a rota passa a função.
   */
  geocode?: (address: string) => Promise<GeocodeResult | null>;
}) {
  const [cep, setCep] = useState(() => formatCep(initialValue?.cep ?? ''));
  const [address, setAddress] = useState<Address>(() => ({
    ...EMPTY_ADDRESS,
    ...initialValue,
  }));
  const [status, setStatus] = useState<Status>('ocioso');
  const [mapOpen, setMapOpen] = useState(false);
  const [locating, setLocating] = useState<Locating>('idle');
  /* Onde o mapa abre quando o Google achou só a região: centro, e não pino. */
  const [hint, setHint] = useState<[number, number] | null>(null);
  /* De onde veio o pino. O que o registro já tinha conta como marcado à mão. */
  const pinSource = useRef<'manual' | 'address' | null>(
    initialValue?.latitude != null && initialValue?.longitude != null ? 'manual' : null,
  );
  /* O endereço da última procura — ou o do pino marcado à mão. Repetir o mesmo
     não chama o Google de novo. */
  const lastLine = useRef<string | null>(
    addressLine({ ...EMPTY_ADDRESS, ...initialValue }),
  );

  function update(patch: Partial<Address>) {
    setAddress((before) => {
      const next = { ...before, ...patch };
      onChange?.(next);
      return next;
    });
  }

  async function locate(a: Address) {
    if (!geocode) return;
    const line = addressLine(a);
    if (line === lastLine.current) return;
    if (line === null) {
      /* Apagaram o número ou a cidade: o pino do endereço não vale mais. O
         marcado à mão fica, e guarda o endereço em que foi marcado. */
      if (pinSource.current === 'address') {
        lastLine.current = null;
        pinSource.current = null;
        update({ latitude: null, longitude: null });
        setLocating('idle');
      }
      return;
    }
    lastLine.current = line;

    if (pinSource.current === 'manual') {
      setLocating('addressChanged');
      return;
    }
    setHint(null);
    if (pinSource.current === 'address') {
      pinSource.current = null;
      update({ latitude: null, longitude: null });
    }

    setLocating('searching');
    try {
      const found = await geocode(line);
      /* A pessoa mudou o endereço enquanto o Google respondia: vale o novo. */
      if (lastLine.current !== line) return;
      if (found === null) {
        setLocating('notFound');
      } else if (found.precise) {
        pinSource.current = 'address';
        update({ latitude: found.lat, longitude: found.lng });
        setLocating('found');
      } else {
        setHint([found.lat, found.lng]);
        setLocating('approximate');
      }
    } catch {
      if (lastLine.current === line) setLocating('failed');
    }
  }

  /** Troca o pino marcado à mão pela posição do endereço atual. */
  function relocate() {
    pinSource.current = null;
    lastLine.current = null;
    void locate(address);
  }

  async function lookup() {
    if (cep.replace(/\D/g, '').length !== 8) return;

    setStatus('buscando');
    try {
      const body = await lookupCep(cep);
      if (body === null) return;
      if (body === 'nao_encontrado') {
        /* O CEP geral da cidade (61600-000) o ViaCEP não acha. A faixa ainda diz
           o estado com certeza — e só o estado: rua e cidade ficam com quem
           digita, e a UF que a pessoa já escolheu não é trocada (27/10/2026). */
        const uf = stateOfCep(cep);
        if (uf) {
          setAddress((before) => {
            if (before.uf) return before;
            const next = { ...before, uf, estado: brazilianStateName(uf) ?? '' };
            onChange?.(next);
            return next;
          });
        }
        setStatus('nao_encontrado');
        return;
      }

      /* O número nunca vem do ViaCEP — preservado do que a pessoa já tinha
         digitado, se tinha. O mesmo vale pro pino: a consulta não sabe onde
         ele está. */
      update({ ...body, cep: body.cep ?? '' });
      setStatus('encontrado');
      /* Com o número já digitado, o endereço fica completo aqui. */
      void locate({ ...address, ...body, cep: body.cep ?? '' });
    } catch {
      setStatus('falhou');
    }
  }

  return (
    <div className="flex flex-col gap-3">
      {title && <p className="rotulo text-texto-suave">{title}</p>}

      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <Field
            label="CEP"
            value={cep}
            placeholder="00000-000"
            inputMode="numeric"
            maxLength={9}
            suffix={
              status === 'buscando' ? <Spin size={16} /> : <Icon nome="busca" size={16} />
            }
            valid={status === 'encontrado' ? 'CEP encontrado.' : undefined}
            error={
              status === 'nao_encontrado'
                ? 'CEP não encontrado — preencha o endereço abaixo à mão.'
                : status === 'falhou'
                  ? 'Não foi possível consultar o CEP agora — preencha o endereço abaixo à mão.'
                  : undefined
            }
            help={
              status === 'ocioso'
                ? 'Não sabe o CEP? Pode deixar em branco e preencher o resto.'
                : undefined
            }
            onChange={(e) => {
              setCep(formatCep(e.target.value));
              if (status !== 'ocioso' && status !== 'buscando') setStatus('ocioso');
            }}
            onBlur={() => void lookup()}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return;
              e.preventDefault();
              void lookup();
            }}
          />
        </div>

        {/* Alinhado à CAIXA do CEP, e não ao pé do campo: embaixo dela vem a
            ajuda ou o erro, e com `items-end` o botão descia até encostar no
            texto. O rótulo invisível repete o do `Field` (`text-xs` e
            `gap-1.5`), para a altura acompanhar a dele. */}
        <div className="flex flex-col gap-1.5">
          <span aria-hidden="true" className="invisible text-xs font-medium">
            CEP
          </span>
          <Button
            type="button"
            variant="contorno"
            icon="mapa"
            aria-label="Marcar posição no mapa"
            title="Marcar posição no mapa"
            onClick={() => setMapOpen(true)}
          />
        </div>
      </div>

      <FormRow cols={[8, 4]}>
        <Field
          label="Logradouro"
          required={required}
          value={address.logradouro}
          onChange={(e) => update({ logradouro: e.target.value })}
          onBlur={() => void locate(address)}
        />
        <Field
          label="Número"
          value={address.numero}
          onChange={(e) => update({ numero: e.target.value })}
          onBlur={() => void locate(address)}
        />
      </FormRow>

      <FormRow cols={[6, 6]}>
        <Field
          label="Complemento"
          value={address.complemento}
          onChange={(e) => update({ complemento: e.target.value })}
        />
        <Field
          label="Bairro"
          value={address.bairro}
          onChange={(e) => update({ bairro: e.target.value })}
          onBlur={() => void locate(address)}
        />
      </FormRow>

      <FormRow cols={[8, 4]}>
        <Field
          label="Cidade"
          required={required}
          value={address.localidade}
          onChange={(e) => update({ localidade: e.target.value })}
          onBlur={() => void locate(address)}
        />
        <Select
          label="Estado"
          required={required}
          value={address.uf}
          onChange={(e) => {
            const uf = e.target.value;
            const estado = brazilianStateName(uf) ?? '';
            update({ uf, estado });
            void locate({ ...address, uf, estado });
          }}
        >
          <option value="">Escolha…</option>
          {BRAZILIAN_STATES.map((code) => (
            <option key={code} value={code}>
              {code} — {BRAZILIAN_STATE_INFO[code].name}
            </option>
          ))}
        </Select>
      </FormRow>

      {locating === 'searching' ? (
        <p className="flex items-center gap-2 text-xs text-texto-suave">
          <Spin size={14} /> Procurando a posição pelo endereço…
        </p>
      ) : (
        address.latitude !== null &&
        address.longitude !== null && (
          <PinPreview
            position={[address.latitude, address.longitude]}
            fromAddress={pinSource.current === 'address'}
            onAdjust={() => setMapOpen(true)}
          />
        )
      )}

      {LOCATING_NOTICE[locating] && (
        <Alert tone="alerta">
          <p>{LOCATING_NOTICE[locating]}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              variant="contorno"
              icon="mapa"
              onClick={() => setMapOpen(true)}
            >
              {locating === 'addressChanged' ? 'Conferir no mapa' : 'Marcar no mapa'}
            </Button>
            {locating === 'addressChanged' && (
              <Button type="button" size="sm" variant="discreto" onClick={relocate}>
                Usar a posição do novo endereço
              </Button>
            )}
          </div>
        </Alert>
      )}

      <MapPicker
        open={mapOpen}
        onOpenChange={setMapOpen}
        uf={address.uf}
        hint={hint}
        initialPosition={
          address.latitude !== null && address.longitude !== null
            ? [address.latitude, address.longitude]
            : null
        }
        onConfirm={(latitude, longitude) => {
          update({ latitude, longitude });
          pinSource.current = 'manual';
          lastLine.current = addressLine(address);
          setHint(null);
          setLocating('idle');
          setMapOpen(false);
        }}
      />
    </div>
  );
}

/* -------------------------------------------------------------------------- *
 * O mapa
 * -------------------------------------------------------------------------- */

/**
 * Onde abrir o mapa quando ainda não há posição marcada — a capital da UF que
 * já se conhece, ou o centro do Brasil quando nem isso existe ainda. Não é
 * geocodificação: é só evitar abrir o mapa mostrando o Atlântico.
 */
// @valor
const STATE_CAPITALS: Record<string, LatLngExpression> = {
  AC: [-9.975, -67.8243],
  AL: [-9.6498, -35.7089],
  AM: [-3.119, -60.0217],
  AP: [0.0349, -51.0694],
  BA: [-12.9777, -38.5016],
  CE: [-3.7172, -38.5433],
  DF: [-15.7939, -47.8828],
  ES: [-20.3155, -40.3128],
  GO: [-16.6869, -49.2648],
  MA: [-2.5297, -44.3028],
  MG: [-19.9167, -43.9345],
  MS: [-20.4697, -54.6201],
  MT: [-15.6014, -56.0979],
  PA: [-1.4558, -48.4902],
  PB: [-7.1195, -34.845],
  PE: [-8.0476, -34.877],
  PI: [-5.0892, -42.8019],
  PR: [-25.4284, -49.2733],
  RJ: [-22.9068, -43.1729],
  RN: [-5.7945, -35.211],
  RO: [-8.7619, -63.9039],
  RR: [2.8235, -60.6758],
  RS: [-30.0346, -51.2177],
  SC: [-27.5954, -48.548],
  SE: [-10.9472, -37.0731],
  SP: [-23.5505, -46.6333],
  TO: [-10.1689, -48.3317],
};

const BRAZIL_CENTER: LatLngExpression = [-14.235, -51.9253];

const PIN_PATH =
  'M12 22s8-7.1 8-13a8 8 0 10-16 0c0 5.9 8 13 8 13zM12 12.5a2.75 2.75 0 100-5.5 2.75 2.75 0 000 5.5z';

/* O contorno branco é o do `RouteMap`: sem ele o pino some sobre rua e quadra
   da mesma cor. */
function pinIcon(): L.DivIcon {
  return L.divIcon({
    className: '',
    html: `<svg width="34" height="34" viewBox="0 0 24 24" fill="currentColor" stroke="white" stroke-width="1.2" class="text-primaria drop-shadow"><path d="${PIN_PATH}"/></svg>`,
    iconSize: [34, 34],
    iconAnchor: [17, 32],
  });
}

/**
 * A posição gravada, à vista: um mapa pequeno e parado com o pino, e de onde
 * ele veio. Coordenada em número ninguém confere; a esquina, sim.
 *
 * Parado de propósito — sem arrastar, sem roda, sem teclado: no meio de um
 * formulário, a roda do mouse rolaria o mapa em vez da página. Ajustar é no
 * mapa grande, pelo botão.
 */
function PinPreview({
  position,
  fromAddress,
  onAdjust,
}: {
  position: [number, number];
  /** Achado pelo endereço, e não marcado à mão. */
  fromAddress: boolean;
  onAdjust: () => void;
}) {
  return (
    <div className="overflow-hidden rounded-borda border border-borda">
      <div className="h-40">
        {/* A chave remonta o mapa quando o pino muda: o `center` do
            `MapContainer` só vale na montagem. */}
        <MapContainer
          key={`${position[0]},${position[1]}`}
          center={position}
          zoom={16}
          dragging={false}
          scrollWheelZoom={false}
          doubleClickZoom={false}
          touchZoom={false}
          boxZoom={false}
          keyboard={false}
          zoomControl={false}
          className="h-full w-full"
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <Marker position={position} icon={pinIcon()} interactive={false} />
        </MapContainer>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-borda px-3 py-2">
        <p className="flex min-w-0 items-center gap-1.5 text-xs text-texto-suave">
          <Icon nome={fromAddress ? 'confirmado' : 'mapa'} size={16} />
          <span>
            {fromAddress ? 'Achada pelo endereço' : 'Marcada no mapa'} ·{' '}
            {position[0].toFixed(6)}, {position[1].toFixed(6)}
          </span>
        </p>
        <Button type="button" size="sm" variant="discreto" icon="mapa" onClick={onAdjust}>
          Ajustar
        </Button>
      </div>
    </div>
  );
}

/** Um clique no mapa marca ou remarca o pino — sem componente visível próprio. */
function ClickToPlace({ onPick }: { onPick: (lat: number, lng: number) => void }) {
  useMapEvents({
    click(e) {
      onPick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

function MapPicker({
  open,
  onOpenChange,
  uf,
  hint,
  initialPosition,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  uf: string;
  /** A região que o Google achou: o mapa abre ali, sem pino. */
  hint: [number, number] | null;
  initialPosition: [number, number] | null;
  onConfirm: (lat: number, lng: number) => void;
}) {
  const [position, setPosition] = useState<[number, number] | null>(initialPosition);
  const containerRef = useRef<HTMLDivElement>(null);

  /* O pino reabre onde a pessoa deixou da última vez; sem isso, reabrir o mapa
     pra ajustar um clique perdia a marcação anterior. */
  useEffect(() => {
    if (open) setPosition(initialPosition);
  }, [open, initialPosition]);

  const center: LatLngExpression =
    initialPosition ?? hint ?? STATE_CAPITALS[uf] ?? BRAZIL_CENTER;
  const zoom = initialPosition ? 15 : hint ? 16 : uf in STATE_CAPITALS ? 12 : 4;

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Marcar posição no mapa"
      descricao="Clique para soltar o pino, ou arraste-o para ajustar."
      size="lg"
    >
      <div
        ref={containerRef}
        className="h-96 overflow-hidden rounded-borda border border-borda"
      >
        {open && (
          <MapContainer center={center} zoom={zoom} className="h-full w-full">
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <ClickToPlace onPick={(lat, lng) => setPosition([lat, lng])} />
            {position && (
              <Marker
                position={position}
                icon={pinIcon()}
                draggable
                eventHandlers={{
                  dragend: (e) => {
                    const { lat, lng } = (e.target as L.Marker).getLatLng();
                    setPosition([lat, lng]);
                  },
                }}
              />
            )}
          </MapContainer>
        )}
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-texto-suave">
          {position
            ? `${position[0].toFixed(6)}, ${position[1].toFixed(6)}`
            : 'Nenhuma posição marcada ainda.'}
        </p>
        <div className="flex gap-2">
          <Button type="button" variant="discreto" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            type="button"
            disabled={!position}
            onClick={() => position && onConfirm(position[0], position[1])}
          >
            Usar esta posição
          </Button>
        </div>
      </div>
    </Modal>
  );
}
